import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel } from "@/components/shared/DashKit";
import { useContent } from "@/context/ContentContext";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, CheckCircle2, MailWarning } from "lucide-react";
import { PaymentNumbers } from "@/components/dash/PaymentNumbers";

export default function Settings() {
  const { user, setUser } = useAuth();
  const { t } = useContent();
  const { t: tr } = useTranslation();
  const [resend, setResend] = useState("idle");
  const resendVerification = async () => {
    setResend("sending");
    try {
      await api.post("/auth/resend-verification", { email: user.email });
      setResend("sent");
    } catch (e) {
      setResend(e?.response?.status === 429 ? "limited" : "error");
    }
  };
  const [name, setName] = useState(user?.name || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    try {
      const body = { name };
      if (password) body.password = password;
      const { data } = await api.put("/profile", body);
      setUser(data); setPassword("");
      toast.success(t("set.saved", "Profil mis à jour"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <PageHeader title={t("set.title", "Paramètres")} subtitle={t("set.subtitle", "Gérez les informations de votre compte.")} />
      <div className="max-w-lg">
        <Panel title={t("set.profile", "Profil")}>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t("set.name", "Nom")}</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" className="mt-1 w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
            </div>
            <div>
              <label className="text-sm font-medium">{t("set.email", "Email")}</label>
              <input value={user?.email} disabled className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-muted-foreground" />
              <div className="mt-2 flex flex-wrap items-center gap-3" data-testid="email-verify-status">
                {user?.email_verified ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {tr("ev.verified")}
                  </span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                      <MailWarning className="h-3.5 w-3.5" /> {tr("ev.unverified")}
                    </span>
                    <button onClick={resendVerification} disabled={resend === "sending"} data-testid="settings-resend-verification"
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-medium transition-colors hover:bg-secondary disabled:opacity-50">
                      {resend === "sending" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      {resend === "sending" ? tr("ev.banner.sending") : tr("ev.banner.resend")}
                    </button>
                    {resend === "sent" && <span className="text-xs text-emerald-600 dark:text-emerald-400">{tr("ev.banner.sent")}</span>}
                    {resend === "limited" && <span className="text-xs text-amber-600 dark:text-amber-400">{tr("ev.banner.limited")}</span>}
                    {resend === "error" && <span className="text-xs text-destructive">{tr("ev.banner.error")}</span>}
                  </>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">{t("set.new_pwd", "Nouveau mot de passe")}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="settings-password" placeholder={t("set.pwd_ph", "Laisser vide pour conserver")} className="mt-1 w-full rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-accent/50" />
            </div>
            <button onClick={save} disabled={loading} data-testid="save-settings-btn" className="flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-accent disabled:opacity-50">{loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("set.save", "Enregistrer")}</button>
          </div>
        </Panel>
        <PaymentNumbers />
      </div>
    </div>
  );
}
